/**
 * Runtime accessibility fixes for Lighthouse / UserWay audits.
 * - Converts Splide ul/li to div (avoids role=group on <li>)
 * - Converts any <ul>/<ol> with invalid non-<li> children to <div>
 * - Blocks incompatible ARIA on any remaining Splide <li> slides
 * - Removes empty id=""
 * - Makes duplicate / empty image alt text unique or decorative
 */
(function () {
  'use strict';

  var unifying = false;

  /* ── 1) Block Splide from setting role=group on <li> ─────────────── */
  var blockedLiAttrs = {
    role: true,
    'aria-roledescription': true,
    'aria-label': true,
    'aria-selected': true,
    'aria-controls': true
  };
  var originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function (name, value) {
    var attr = String(name).toLowerCase();
    if (
      this.tagName === 'LI' &&
      this.classList &&
      this.classList.contains('splide__slide') &&
      blockedLiAttrs[attr]
    ) {
      if (attr === 'role' && value === 'listitem') {
        return originalSetAttribute.call(this, name, value);
      }
      return;
    }
    return originalSetAttribute.call(this, name, value);
  };

  function replaceElementTag(el, tagName) {
    if (!el || !el.parentNode || el.tagName === tagName.toUpperCase()) return el;
    var next = document.createElement(tagName);
    for (var i = 0; i < el.attributes.length; i++) {
      var attr = el.attributes[i];
      next.setAttribute(attr.name, attr.value);
    }
    while (el.firstChild) {
      next.appendChild(el.firstChild);
    }
    el.parentNode.replaceChild(next, el);
    return next;
  }

  function stripIncompatibleSlideAttrs(slide) {
    if (!slide) return;
    var role = slide.getAttribute('role');
    if (slide.tagName === 'LI' && role && role !== 'listitem') {
      slide.removeAttribute('role');
      slide.removeAttribute('aria-selected');
      slide.removeAttribute('aria-controls');
      slide.removeAttribute('aria-label');
      slide.removeAttribute('aria-roledescription');
      slide.removeAttribute('tabindex');
    }
  }

  /* Convert ul.splide__list / li.splide__slide → div before/while Splide mounts */
  function normalizeSplideMarkup(root) {
    var scope = root || document;
    var slides = scope.querySelectorAll('li.splide__slide');
    for (var s = 0; s < slides.length; s++) {
      var slide = replaceElementTag(slides[s], 'div');
      stripIncompatibleSlideAttrs(slide);
    }
    var lists = scope.querySelectorAll('ul.splide__list');
    for (var l = 0; l < lists.length; l++) {
      var list = replaceElementTag(lists[l], 'div');
      if (list) {
        var role = list.getAttribute('role');
        if (role === 'presentation' || role === 'none' || role === 'list') {
          list.removeAttribute('role');
        }
        list.removeAttribute('aria-orientation');
      }
    }
  }

  function cleanSplideSlide(slide) {
    if (!slide || !slide.classList || !slide.classList.contains('splide__slide')) return;
    if (slide.tagName === 'LI') {
      slide = replaceElementTag(slide, 'div') || slide;
    }
    stripIncompatibleSlideAttrs(slide);
  }

  function cleanSplideLists() {
    normalizeSplideMarkup(document);
    document.querySelectorAll('.splide__list').forEach(function (list) {
      if (list.tagName === 'UL') {
        list = replaceElementTag(list, 'div') || list;
      }
      var role = list.getAttribute('role');
      if (role === 'presentation' || role === 'none' || role === 'list') {
        list.removeAttribute('role');
        list.removeAttribute('aria-label');
        list.removeAttribute('aria-labelledby');
        list.removeAttribute('aria-activedescendant');
      }
      if (role !== 'tablist') {
        list.removeAttribute('aria-orientation');
      }
      Array.prototype.forEach.call(list.children, function (child) {
        if (child.classList && child.classList.contains('splide__slide')) {
          cleanSplideSlide(child);
        }
      });
    });
    document.querySelectorAll('li.splide__slide').forEach(cleanSplideSlide);
  }

  /* ── 2) Empty id="" ──────────────────────────────────────────────── */
  function fixEmptyIds(root) {
    var allIds = (root || document).querySelectorAll('[id]');
    for (var i = 0; i < allIds.length; i++) {
      var idVal = allIds[i].getAttribute('id');
      if (!idVal || !idVal.trim()) {
        allIds[i].removeAttribute('id');
      }
    }
  }

  /* ── 3) Unique image alt text ────────────────────────────────────── */
  function altFromSrc(img) {
    try {
      var src = img.currentSrc || img.getAttribute('src') || img.src || '';
      if (!src) return '';
      if (/facebook\.com\/tr|google-analytics|googletagmanager|doubleclick/i.test(src)) {
        return '';
      }
      var file = src.split('/').pop().split('?')[0].replace(/\.[a-z0-9]+$/i, '');
      return file
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, function (c) {
          return c.toUpperCase();
        })
        .trim();
    } catch (e) {
      return '';
    }
  }

  function markDecorative(img) {
    img.setAttribute('alt', '');
    img.setAttribute('role', 'presentation');
    img.setAttribute('aria-hidden', 'true');
  }

  function uniquifyImageAlts(root) {
    var scope = root || document;
    var seen = {};
    scope.querySelectorAll('img').forEach(function (img, index) {
      var width = parseInt(img.getAttribute('width'), 10) || img.width || 0;
      var height = parseInt(img.getAttribute('height'), 10) || img.height || 0;
      var src = img.getAttribute('src') || '';

      if (
        (width > 0 && width <= 1 && height > 0 && height <= 1) ||
        /facebook\.com\/tr|google-analytics|googletagmanager|doubleclick/i.test(src)
      ) {
        markDecorative(img);
        return;
      }

      if (!img.hasAttribute('alt')) {
        img.setAttribute('alt', altFromSrc(img) || 'Image ' + (index + 1));
      }

      var alt = img.getAttribute('alt');
      if (alt === null) return;

      if (alt === '') {
        var parentLink = img.closest('a');
        var linkName = parentLink
          ? (parentLink.getAttribute('aria-label') || parentLink.textContent || '').trim()
          : '';
        if (linkName || img.getAttribute('role') === 'presentation' || img.getAttribute('aria-hidden') === 'true') {
          markDecorative(img);
          return;
        }
        var derived = altFromSrc(img);
        if (derived) {
          img.setAttribute('alt', derived);
          alt = derived;
        } else {
          markDecorative(img);
          return;
        }
      }

      if (Object.prototype.hasOwnProperty.call(seen, alt)) {
        seen[alt] += 1;
        img.setAttribute('alt', alt + ' (' + seen[alt] + ')');
      } else {
        seen[alt] = 1;
      }
    });
  }

  /* ── 4) Lists must only contain <li> / <script> / <template> ─────── */
  function fixInvalidListChildren(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll('ul, ol');
    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      var children = list.children;
      var invalid = false;
      for (var c = 0; c < children.length; c++) {
        var tag = children[c].tagName;
        if (tag !== 'LI' && tag !== 'SCRIPT' && tag !== 'TEMPLATE') {
          invalid = true;
          break;
        }
      }
      if (invalid) {
        // Prefer converting the list container to a div (keeps layout classes, fixes Lighthouse)
        replaceElementTag(list, 'div');
      }
    }
  }

  /* ── 5) Shift focus to Promotional Popups ──────────────────────── */
  var popupFocused = false;
  function trapPopupFocus(root) {
    var scope = root || document;
    if (popupFocused) return;
    
    // Look for common third-party popup wrappers (Klaviyo, JustUno, etc.)
    var popup = scope.querySelector('div[role="dialog"], form.needsclick, [data-testid="form-component"]');
    if (popup && popup.offsetParent !== null) {
      // Find the email input or close button
      var focusable = popup.querySelector('input[type="email"], input[type="text"], button, [tabindex="0"]');
      if (focusable && document.activeElement !== focusable) {
        focusable.focus();
        popupFocused = true;
      }
    }
  }

  function runAll(root) {
    if (unifying) return;
    unifying = true;
    try {
      trapPopupFocus(root);
      // cleanSplideLists(); // Disabled because it breaks continuous scroll
      // fixInvalidListChildren(root); // Disabled because it breaks sliders
      fixEmptyIds(root);
      uniquifyImageAlts(root);
    } finally {
      unifying = false;
    }
  }

  runAll();

  if ('MutationObserver' in window) {
    var scheduled = false;
    new MutationObserver(function () {
      if (unifying || scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        runAll();
      });
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'aria-roledescription', 'alt', 'id', 'href']
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    runAll();
  });
  window.addEventListener('load', function () {
    runAll();
    var n = 0;
    var t = setInterval(function () {
      runAll();
      if (++n > 30) clearInterval(t);
    }, 300);
  });
})();
